import React, { useCallback, useEffect, useState } from "react";

import Modal from "../modal/Modal";
import { hasSessionSwitched, syncSessionBaseline } from "../../common/utils";

/**
 * ブラウザのサインイン状態が別の場所で切り替わったことを検知し、
 * 読み込み直しを促すガード。App 直下に 1 つだけマウントする。
 *
 * auth_token Cookie とデモ状態（localStorage）はブラウザ全体で共有されるため、
 * 「別タブでデモリンクを開く」「戻るボタンで前のアカウントの画面に戻る
 * （bfcache 復帰）」といった操作をすると、描画済みの画面だけが切り替え前の
 * ユーザーのまま取り残される。この状態では画面に表示されているユーザーと
 * 実際に API を叩くユーザーが食い違い、「自分のアカウントが表示されているのに
 * デモモードの警告が出る」ような混乱が起きる。
 *
 * 検知したら操作を続けさせず、モーダル（× なし・オーバーレイクリックでも
 * 閉じない）で読み込み直しへ誘導する。自動リロードにはしない — 編集中の
 * 入力を予告なく失わせないため、実行はユーザーのクリックに委ねる。
 */

// Cookie の変更には通知イベントが無いため、表示中のタブでは定期確認する。
// 読み取りは document.cookie と localStorage の 1 回ずつで軽い。
const POLL_INTERVAL_MS = 3000;

const SessionSwitchGuard: React.FC = () => {
  const [isSwitched, setIsSwitched] = useState(false);

  useEffect(() => {
    // この画面が描画に使ったセッションを基準として記録する
    syncSessionBaseline();

    const check = () => {
      if (hasSessionSwitched()) {
        setIsSwitched(true);
      }
    };
    const checkIfVisible = () => {
      if (!document.hidden) {
        check();
      }
    };

    // storage:           別タブでのデモ状態の開始・解除（localStorage）
    // pageshow:          戻る / 進むでの bfcache 復帰
    // focus / visibility: 別タブで操作してからこのタブへ戻ってきたとき
    window.addEventListener("storage", check);
    window.addEventListener("pageshow", check);
    window.addEventListener("focus", checkIfVisible);
    document.addEventListener("visibilitychange", checkIfVisible);
    const intervalId = window.setInterval(checkIfVisible, POLL_INTERVAL_MS);

    return () => {
      window.removeEventListener("storage", check);
      window.removeEventListener("pageshow", check);
      window.removeEventListener("focus", checkIfVisible);
      document.removeEventListener("visibilitychange", checkIfVisible);
      window.clearInterval(intervalId);
    };
  }, []);

  const handleReload = useCallback(() => window.location.reload(), []);

  if (!isSwitched) {
    return null;
  }

  return (
    <Modal
      variant="dialog"
      title="サインイン状態が変わりました"
      description={
        "別のタブでの操作・デモモードへの切り替え・有効期限切れのいずれかにより、このブラウザのサインイン状態が変わりました。\nこの画面には変更前の内容が残っているため、読み込み直してからご利用ください。"
      }
      primaryAction={{
        label: "読み込み直す",
        onClick: handleReload,
      }}
    />
  );
};

export default SessionSwitchGuard;
